import { toPng } from "html-to-image";
import { CRS, Map } from "leaflet";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { MapContainer } from "react-leaflet";

import { Document } from "../../types/document";
import { Crop, MonitorPinAbbr } from "../../types/map";

import MapContent from "./MapContent";
import styles from "./pin-crop-map.module.css";

interface Prop {
  map: Document;
  crop: Crop | Crop[];
  monitorPinAbbr: MonitorPinAbbr | MonitorPinAbbr[];
  isDraggable?: boolean;
  zoom?: boolean;
  onCropChange: (crop: Crop) => void;
  onPinClick?: (index: number) => void;
  isPinSelected?: boolean;
  selectedPinIndex?: number | null;
}

export interface PinCropMapHandle {
  exportImage: () => Promise<string | null>;
}

const PinCropMap = forwardRef<PinCropMapHandle, Prop>((prop, ref) => {
  const {
    map,
    crop,
    monitorPinAbbr,
    isDraggable,
    zoom,
    onCropChange,
    onPinClick,
    isPinSelected,
    selectedPinIndex,
  } = prop;
  const mapContainerRef = useRef<Map | null>(null);

  const imageHeight = map?.imageHeight || 250;
  const imageWidth = map?.imageWidth || 250;

  useEffect(() => {
    if (Array.isArray(crop)) return;

    if (crop.x === 1 && crop.y === 1) {
      const newCrop = { x: imageWidth / 2, y: imageHeight / 2 };
      onCropChange(newCrop);
    }
  }, [crop, imageWidth, imageHeight, onCropChange]);

  useImperativeHandle(ref, () => ({
    exportImage: async () => {
      const leafletMap = mapContainerRef.current;

      if (!leafletMap) {
        throw new Error("Leaflet map is not initialized.");
      }

      leafletMap.invalidateSize();

      const mapElement = leafletMap.getContainer();

      const markerElements = Array.from(
        mapElement.querySelectorAll<HTMLElement>(".leaflet-marker-icon")
      );

      const originalStyles = markerElements.map((marker) => {
        const content = marker.firstElementChild as HTMLElement | null;

        if (!content) return null;

        const originalTransform = content.style.transform;
        const originalTransformOrigin = content.style.transformOrigin;

        content.style.transform = `scale(0.5)`;
        content.style.transformOrigin = "bottom center";

        return {
          content,
          originalTransform,
          originalTransformOrigin,
        };
      });

      let fullImage: string;

      try {
        fullImage = await toPng(mapElement, {
          cacheBust: true,
          pixelRatio: 3,
          backgroundColor: "#ffffff",
          filter: (node) => {
            if (node instanceof HTMLElement) {
              return (
                !node.classList.contains("leaflet-control-zoom") &&
                !node.classList.contains("leaflet-control-attribution")
              );
            }

            return true;
          },
        });
      } finally {
        // Restore the original pin styles after capturing the map.
        originalStyles.forEach((style) => {
          if (!style) return;

          style.content.style.transform = style.originalTransform;
          style.content.style.transformOrigin = style.originalTransformOrigin;
        });
      }
      const overlay = mapElement.querySelector(
        ".leaflet-image-layer"
      ) as HTMLImageElement | null;

      if (!overlay) {
        return fullImage;
      }

      const mapRect = mapElement.getBoundingClientRect();

      let minLeft = overlay.getBoundingClientRect().left - mapRect.left;
      let minTop = overlay.getBoundingClientRect().top - mapRect.top;
      let maxRight = minLeft + overlay.getBoundingClientRect().width;
      let maxBottom = minTop + overlay.getBoundingClientRect().height;

      const markers = mapElement.querySelectorAll(".leaflet-marker-icon");

      markers.forEach((marker) => {
        const rect = marker.getBoundingClientRect();

        const left = rect.left - mapRect.left;
        const top = rect.top - mapRect.top;
        const right = left + rect.width;
        const bottom = top + rect.height;

        minLeft = Math.min(minLeft, left);
        minTop = Math.min(minTop, top);
        maxRight = Math.max(maxRight, right);
        maxBottom = Math.max(maxBottom, bottom);
      });

      const padding = 8;

      minLeft = Math.max(0, minLeft - padding);
      minTop = Math.max(0, minTop - padding);
      maxRight = Math.min(mapElement.clientWidth, maxRight + padding);
      maxBottom = Math.min(mapElement.clientHeight, maxBottom + padding);

      const image = new window.Image();

      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () =>
          reject(new Error("Failed to load exported map image."));
        image.src = fullImage;
      });

      const scaleX = image.naturalWidth / mapElement.clientWidth;
      const scaleY = image.naturalHeight / mapElement.clientHeight;

      const sourceX = Math.floor(minLeft * scaleX);
      const sourceY = Math.floor(minTop * scaleY);
      const sourceRight = Math.ceil(maxRight * scaleX);
      const sourceBottom = Math.ceil(maxBottom * scaleY);

      const cropWidth = sourceRight - sourceX;
      const cropHeight = sourceBottom - sourceY;

      const canvas = window.document.createElement("canvas");
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return fullImage;
      }

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, cropWidth, cropHeight);

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      return canvas.toDataURL("image/png");
    },
  }));

  return (
    <MapContainer
      center={[imageHeight / 2, imageWidth / 2]}
      crs={CRS.Simple}
      minZoom={-10}
      zoom={0}
      zoomSnap={0}
      maxZoom={12}
      scrollWheelZoom={zoom ?? true}
      touchZoom={zoom ?? true}
      zoomControl={zoom ?? true}
      dragging={isDraggable ?? true}
      className={styles["map-container"]}
      ref={mapContainerRef}
    >
      {crop && monitorPinAbbr && (
        <MapContent
          imageHeight={imageHeight}
          imageWidth={imageWidth}
          propertyMap={map}
          monitorPinAbbr={monitorPinAbbr}
          crop={crop}
          onCropChange={onCropChange}
          isDraggable={isDraggable}
          onPinClick={onPinClick ? onPinClick : () => {}}
          isPinSelected={isPinSelected}
          selectedPinIndex={selectedPinIndex}
        />
      )}
    </MapContainer>
  );
});

PinCropMap.displayName = "PinCropMap";

export default PinCropMap;
